import './AlertsList.scss';
import {forwardRef, useEffect,useState} from 'react';
import {LegacyByzzerButton, ByzzerModal} from '../form';
import {alertConfigs} from '@/config/alertDetails.config';
import { alertsDescriptionUrl } from '@/config/globalVars';
import ReportInformation from '@/views/ReportInformation';
import {ByzzerMask} from '@/components/ByzzerMask/ByzzerMask';
import classnames from 'classnames';
import {useUser} from '@/contexts/UserContext';
import {getAllAlerts} from '@/services/product.service';
import {useBetterNavigate} from '@/utils/utils';
import StackedReportFilters from '@/views/MyReports/StackedReportFilters';
import { MyAlertsProps } from '@/pages/MyAlertsPage';
import useIsMobile from '@/hooks/useIsMobile';
import { ByzzerCard } from '../ByzzerCard';
import {TipIcon} from '@/components/icons';
import { Link } from 'react-router-dom';

const baseClassName = 'alert-setup-wizard';
const AlertsList = forwardRef<any, MyAlertsProps>(({
    alertLimits: {
        totalConfiguredAlerts,
        maxTotalAlerts,
        canAddAlerts,
    },
    ...props
}, ref) => {
    const isMobile = useIsMobile();
    const {subscribedSkus, isCsrObo, subscription, user, company} = useUser();
    const [loading, setLoading] = useState(false);
    const [sortedAlertsWithSubscribedFlag, setSortedAlertsWithSubscribedFlag] = useState<(ProductWithMeta & {subscribed?: boolean})[]>([]);
    const [alertInformationView, setAlertInformationView] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<any>();
    const navigate = useBetterNavigate();
    const [skuFilter, setSkuFilter] = useState<string[]>([]);
    const [filteredAlerts, setFilteredAlerts] = useState<typeof sortedAlertsWithSubscribedFlag>([]);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        filterBySkus(skuFilter);
    }, [sortedAlertsWithSubscribedFlag, skuFilter]);

    function filterBySkus(skus: string[]) {
        const filteredAlertsList = sortedAlertsWithSubscribedFlag?.filter(({sku}) => skus?.includes(sku));
        setFilteredAlerts(filteredAlertsList);
    }

    async function loadData() {
        try {
            setLoading(true);

            const allAlerts = getAccessibleAlerts(getAllAlerts());
            const allAlertSkus = allAlerts.map(({sku}) => sku);
            if (skuFilter) {
                setSkuFilter(allAlertSkus);
            }
            setSortedAlertsWithSubscribedFlag(
                allAlerts
                    .map(anAlert => ({ ...anAlert, subscribed: subscribedSkus.includes(anAlert.sku)}))
                    .toSorted((a, b) => a.title.localeCompare(b.title))
            );
        } catch (err) {
            console.error(`AlertsList - loadData error - 'err' ===>>`, err);
        } finally {
            setLoading(false);
        }
    }

    const getAccessibleAlerts = (alerts: ProductWithMeta[]) => {
        return alerts.filter((alrt) => company?.accessLevel ? alrt.accessLevels.includes(company.accessLevel) : true);
    }


    
    const renderSmartTag = (alertDetail) => {
        if (alertDetail.sku) {
            const getAlertConfigs = alertConfigs.find((val) => val.productSku === alertDetail.sku);
            if (getAlertConfigs) {
                if (getAlertConfigs.alertType === 'Smart') {
                    return 'Smart';
                }
            }
        }
        return ''
    };

    function AlertCard({ alert }) {
        const itemLocked = !alert?.subscribed || !canAddAlerts || !subscription?.active || user?.role === 'viewer';
        const content = (
            <ByzzerCard
                key={alert.sku}
                onClick={!alert.subscribed && !isCsrObo ? () => window.open('https://store.byzzer.com/') : () => {}}
                shouldTrackClick={true}
                trackClickName={`My Reports Report (${alert.title}) clicked`}
                title={alert.title}
                description={alert.description}
                thumbnailUrl={alert.thumbnailUrl}
                thumbnailImageSize={'xs'}
                thumbnailAreaSize={'lg'}
                isLocked={itemLocked}
                actions={
                    <>
                        <TipIcon
                            trackClick={`Report Info (${alert.title}) clicked`}
                            className={undefined}
                            children={undefined}
                            onClick={() => {
                                setAlertInformationView(!alertInformationView);
                                setSelectedAlert(alert);
                            }}
                        onContextMenu={(event)=>{event.preventDefault()}}

                        />
                    </>
                }
                badges={[renderSmartTag(alert)]}
            />
        );
        const searchParam = new URLSearchParams({ runType: 'alert', sku: alert.sku }).toString();

        return alert?.subscribed ? (
            <Link
                to={{ pathname: '/dashboard/configure_alert', search: searchParam }}
                className={'alerts-list__card-container'}
            >
                {' '}
                {content}{' '}
            </Link>
        ) : (
            <>{content}</>
        );
    }

    const renderAlertsList = () => {
        return (
            <div className='alert-groups'>
                {selectedAlert?.title && (
                    <ByzzerModal
                        show={alertInformationView}
                        onClose={() => setAlertInformationView(!alertInformationView)}
                        heading={selectedAlert?.title}
                        size={'large'}
                        headerType={'normal'}
                    >
                        {/* @ts-ignore */}
                        <ReportInformation
                            sku={selectedAlert?.sku}
                            url={selectedAlert?.detailsUrl || `${alertsDescriptionUrl}/${selectedAlert?.sku}.html`}
                        />
                    </ByzzerModal>
                )}
                <div className={'alerts-list-grid-container'} key={filteredAlerts.length}>
                    {filteredAlerts.reduce((accum, alert) => {
                        if (alert.sku === '453') {
                            return accum;
                        }

                        return [
                            ...accum,
                            <AlertCard alert={alert} key={alert.id} />
                        ];
                    }, [] as JSX.Element[])}
                </div>
            </div>
        );
    };

    function handlePurchaseMoreAlerts() {
        if (!isCsrObo) {
            window.open('https://store.byzzer.com/')
        }
    }

    const showFiltersPanel = !isMobile;

    return <div className={classnames(baseClassName)}>
        <ByzzerMask loading={loading}/>
        <header className={`${baseClassName}__header`}>
            <div className={`${baseClassName}__header-text`}>
                <h2 className={`${baseClassName}__step-title`}>Select Alert Type</h2>
                <div className={'alert-configuration-heading__description'}>
                    Your subscription comes with {maxTotalAlerts || 'no'} weekly
                    alert{maxTotalAlerts === 1 ? '' : 's'}.  Configure your alerts to start 
                    receiving them right away - this includes selecting which types of alerts to receive, and 
                    setting the data scope for each of them.

                    <br/>
                    {canAddAlerts &&
                        `You can configure ${maxTotalAlerts - totalConfiguredAlerts} additional alert${maxTotalAlerts - totalConfiguredAlerts === 1 ? '' : 's'}.`
                    }
                </div>
            </div>

            {!canAddAlerts && (
                <div className={classnames(`${baseClassName}__purchase-button`)}>
                    <LegacyByzzerButton
                        label={Boolean(maxTotalAlerts) ? 'Purchase More Alerts' : 'Purchase Alerts'}
                        onClick={handlePurchaseMoreAlerts}
                    />
                </div>
            )}
        </header>

        <div className={classnames(`${baseClassName}__step`, 'alerts-list-container')}>
            {renderAlertsList()}
            {showFiltersPanel&& (
                <StackedReportFilters 
                    onFilter={setSkuFilter}
                    appliesTo='alert'
                    className="alerts-list-options-wrapper"
                    changeReport={false}
                />
            )}
        </div>
    </div>
});

export default AlertsList;
